<template>
  <div :class="$style.inputContainer">
    <InputWithLabel
      ref="input"
      :class="$style.datePickerInput"
      :errorStyleExceptions="$style.hidden"
      label="Time Range"
      name="Time Range"
      readonly
    />
    <div
      v-if="quickPickDateTime"
      :class="$style.inputText"
    >
      {{ quickPicksMetadata[quickPickDateTime].displayName }}
    </div>
    <div
      v-else
      :class="$style.inputText"
    >
      {{ formatDateTime(startDateTime) }}
      <span :class="$style.textSeparator">to</span>
      {{ formatDateTime(endDateTime) || 'Now' }}
    </div>
    <BTooltip
      ref="tooltip"
      placement="bottomright"
      :target="() => $refs.input"
      triggers="focus"
      @show="handleTooltipShow"
    >
      <!-- eslint-disable vue/attribute-hyphenation -->
      <div :class="$style.datePickerContainer">
        <div :class="$style.quickPicksContainer">
          <h6 :class="[$style.sectionHeader, $style.quickPicksHeader]">
            Quick Picks
          </h6>
          <a
            v-for="(item, i) in getQuickPicks"
            :key="i"
            :class="$style.quickPickLink"
            @click="handleQuickPickClick(item)"
          >
            {{ quickPicksMetadata[item].displayName }}
          </a>
        </div>
        <div :class="$style.startEndContainer">
          <h6 :class="$style.sectionHeader">
            Specific Dates
          </h6>
          <div :class="$style.inputGroup">
            <label :class="$style.inputLabel">
              Start Date
            </label>
            <datepicker
              ref="startDatePicker"
              v-model="startDate"
              :calendar-class="$style.calendar"
              :class="$style.dateInput"
              :disabled-dates="{
                from: today,
                to: startOfFirstDateInsightsAvailable,
              }"
              :format="dateFormat"
              name="startDatePicker"
              :typeable="true"
              @input="handleStartDateChange"
            />
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" :class="$style.dateInputIcon" v-bind:svg-inline="''" @click="openStartDatePicker" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M2.462 0v1.23h-.616A1.853 1.853 0 000 3.078v11.077C0 15.168.832 16 1.846 16h12.308A1.853 1.853 0 0016 14.154V3.077a1.853 1.853 0 00-1.846-1.846h-.616V0h-1.23v1.23H3.692V0h-1.23zm-.616 2.462h.616v1.23h1.23v-1.23h8.616v1.23h1.23v-1.23h.616c.346 0 .615.269.615.615v1.846H1.231V3.077c0-.346.269-.615.615-.615zm-.615 3.692h13.538v8a.607.607 0 01-.615.615H1.846a.608.608 0 01-.615-.615v-8zm2.692 1.23v2.231h2.23v-2.23h-2.23zm3.077 0v2.231h2.23v-2.23H7zm3 0v2.231h2.23v-2.23H10z" fill="currentColor"/></svg>
            <transition :name="$style.slideTransition">
              <div
                v-show="v2Errors.has(fieldNames.startDate)"
                :class="$style.error"
              >
                {{ v2Errors.first(fieldNames.startDate) || '' }}
              </div>
            </transition>
          </div>
          <div :class="$style.inputGroup">
            <InputValidator
              :class="$style.timeInput"
              :config="{
                label: 'Start Time',
                maxlength: 5,
                name: 'Start Time',
                validate: 'time',
                value: startTime,
              }"
              @InputValidatorModel="handleStartTimeChange"
            />
          </div>
          <div :class="$style.inputGroup">
            <label :class="$style.inputLabel">
              End Date
            </label>
            <datepicker
              ref="endDatePicker"
              v-model="endDate"
              :calendar-class="$style.calendar"
              :class="$style.dateInput"
              :disabled-dates="{
                to: startOfFirstDateInsightsAvailable,
              }"
              :format="dateFormat"
              name="endDatePicker"
              :typeable="true"
              @input="v => handleDateChange(fieldNames.endDate, v)"
            />
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" :class="$style.dateInputIcon" v-bind:svg-inline="''" @click="openEndDatePicker" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M2.462 0v1.23h-.616A1.853 1.853 0 000 3.078v11.077C0 15.168.832 16 1.846 16h12.308A1.853 1.853 0 0016 14.154V3.077a1.853 1.853 0 00-1.846-1.846h-.616V0h-1.23v1.23H3.692V0h-1.23zm-.616 2.462h.616v1.23h1.23v-1.23h8.616v1.23h1.23v-1.23h.616c.346 0 .615.269.615.615v1.846H1.231V3.077c0-.346.269-.615.615-.615zm-.615 3.692h13.538v8a.607.607 0 01-.615.615H1.846a.608.608 0 01-.615-.615v-8zm2.692 1.23v2.231h2.23v-2.23h-2.23zm3.077 0v2.231h2.23v-2.23H7zm3 0v2.231h2.23v-2.23H10z" fill="currentColor"/></svg>
            <transition :name="$style.slideTransition">
              <div
                v-show="v2Errors.has(fieldNames.endDate) || v2Errors.has(fieldNames.dateTimeOrder)"
                :class="$style.error"
              >
                {{
                  v2Errors.first(fieldNames.endDate)
                    || v2Errors.first(fieldNames.dateTimeOrder) || ''
                }}
              </div>
            </transition>
          </div>
          <div :class="$style.inputGroup">
            <InputValidator
              :class="$style.timeInput"
              :config="{
                label: 'End Time',
                maxlength: 5,
                name: 'End Time',
                validate: 'time',
                value: endTime,
              }"
              @InputValidatorModel="handleEndTimeChange"
            />
          </div>
          <FlatActionButton
            :action="handleApplyClick"
            :class="$style.applyButton"
          >
            apply
          </FlatActionButton>
        </div>
      </div>
    </BTooltip>
  </div>
</template>

<script>
import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import {
  quickPicksMetadata,
  quickPicksOptions,
} from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import BTooltip from 'bootstrap-vue-old-rc/es/components/tooltip/tooltip';
import Datepicker from 'vuejs-datepicker';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import InputValidator from 'components/FormValidation/InputValidator.vue';
import InputWithLabel from 'components/FormValidation/Input.vue';
import InsightsAccessMixin from 'client/Insights/InsightsAccessMixin';
import moment from 'moment';

const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const dateFormat = 'MMM DD, YYYY';
const timeFormat = 'HH:mm';

export default {
  name: 'ActivityLogDatePicker',

  components: {
    BTooltip,
    Datepicker,
    FlatActionButton,
    InputValidator,
    InputWithLabel,
  },

  mixins: [InsightsAccessMixin],

  data: () => ({
    endDate: '',
    endTime: '',
    fieldNames: {
      endDate: 'End Date',
      startDate: 'Start Date',
      dateTimeOrder: 'Date Time Order',
    },
    quickPicksMetadata,
    startDate: '',
    startTime: '',
    today: moment().toDate(),
  }),

  computed: {
    ...mapState(insightsEventModule, {
      endDateTime: 'endDate',
      quickPickDateTime: 'quickPickDate',
      startDateTime: 'startDate',
    }),

    getQuickPicks() {
      if (this.directoryInsightsPremium) {
        return [
          quickPicksOptions.today,
          quickPicksOptions.fifteenMinutes,
          quickPicksOptions.oneHour,
          quickPicksOptions.sixHours,
          quickPicksOptions.twelveHours,
          quickPicksOptions.twentyFourHours,
          quickPicksOptions.sevenDays,
          quickPicksOptions.thirtyDays,
          quickPicksOptions.thisMonth,
          quickPicksOptions.ninetyDays,
        ];
      }
      return [
        quickPicksOptions.today,
        quickPicksOptions.fifteenMinutes,
        quickPicksOptions.oneHour,
        quickPicksOptions.threeHours,
        quickPicksOptions.sixHours,
        quickPicksOptions.twelveHours,
        quickPicksOptions.twentyFourHours,
        quickPicksOptions.sevenDays,
        quickPicksOptions.fifteenDays,
      ];
    },

    startOfFirstDateInsightsAvailable() {
      return new Date(moment(this.firstDateInsightsAvailable).startOf('day').toDate());
    },
  },

  created() {
    // Add validation rules to vee-validate
    this.$validator.extend('time', {
      getMessage: 'Please enter a valid time',
      validate: this.isTimeValid,
    });

    // Use custom error message for validation
    this.$validator.localize('en', {
      custom: {
        'End Time': { required: 'Please enter an end time' },
        'Start Time': { required: 'Please enter a start time' },
      },
    });
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'setEndDate',
      'setQuickPickDate',
      'setStartDate',
    ]),

    dateFormat(date) {
      return moment(date).format(dateFormat);
    },

    formatDateTime(date) {
      if (!(date instanceof Date)) return '';
      return moment(date).format(`${dateFormat} [@] ${timeFormat}`);
    },

    getDateTimeFromInput(dateStr, timeStr) {
      if (!dateStr || !this.isTimeValid(timeStr)) return null;
      const dateTime = moment(dateStr, dateFormat);
      if (timeStr.includes(':')) {
        dateTime.hour(timeStr.split(':')[0]);
        dateTime.minute(timeStr.split(':')[1]);
      } else {
        dateTime.hour(timeStr.slice(0, timeStr.length - 2));
        dateTime.minute(timeStr.slice(timeStr.length - 2));
      }
      return dateTime.toDate();
    },

    handleApplyClick() {
      /**
       * Manually check that the time inputs are valid because vee-validate doesn't update the
       * `errors` object immediately when closing the tooltip
       */
      if (this.v2Errors.any() || this.$refs.tooltip.v2Errors.any()) return;

      this.setQuickPickDate('');

      const endDateTime = this.getDateTimeFromInput(this.endDate, this.endTime);
      const startDateTime = this.getDateTimeFromInput(this.startDate, this.startTime);
      this.setEndDate(endDateTime);
      this.setStartDate(startDateTime);

      this.$emit('change');

      this.$refs.tooltip.$emit('close');

      EventHelper.triggerActivityLogDateRangeChanged({
        durationInMinutes: (endDateTime - startDateTime) / 60000,
        endDate: endDateTime,
        startDate: startDateTime,
      });
    },

    handleDateChange(fieldName, date) {
      this.v2Errors.remove(fieldName);
      if (!moment(date, `${dateFormat} ${timeFormat}`).isValid()) {
        this.v2Errors.add({ field: fieldName, msg: 'Invalid date' });
      }
      if (date < this.startOfFirstDateInsightsAvailable) {
        this.v2Errors.add({
          field: fieldName,
          msg: 'No data available before this date',
        });
      }

      this.handleDateTimeChange();
    },

    handleDateTimeChange() {
      // Validate that the end date falls after the start date. If not, show error
      this.v2Errors.remove(this.fieldNames.dateTimeOrder);
      const endDateTime = this.getDateTimeFromInput(this.endDate, this.endTime);
      const startDateTime = this.getDateTimeFromInput(this.startDate, this.startTime);
      if (endDateTime && startDateTime && endDateTime < startDateTime) {
        this.v2Errors.add({
          field: this.fieldNames.dateTimeOrder,
          msg: 'End date must be after start date',
        });
      }
    },

    handleEndTimeChange(value) {
      this.endTime = value;
      this.handleDateTimeChange();
    },

    handleQuickPickClick(quickPick) {
      this.setQuickPickDate(quickPick);

      this.$emit('change');

      this.$refs.tooltip.$emit('close');

      const startDateTime = quickPicksMetadata[quickPick].startDate();
      const endDateTime = moment().toDate();

      EventHelper.triggerActivityLogDateRangeChanged({
        durationInMinutes: (endDateTime - startDateTime) / 60000,
        endDate: endDateTime,
        startDate: startDateTime,
        quickPickDate: quickPicksMetadata[quickPick].displayName,
      });
    },

    handleStartDateChange(date) {
      this.handleDateChange(this.fieldNames.startDate, date);
      if (date > moment().toDate()) {
        this.v2Errors.add({
          field: this.fieldNames.startDate,
          msg: 'Start date cannot be in the future',
        });
      }
      if (date < moment().startOf('day').subtract(90, 'day').toDate()) {
        this.v2Errors.add({
          field: this.fieldNames.startDate,
          msg: 'Start date cannot be more than 90 days ago',
        });
      }
    },

    handleStartTimeChange(value) {
      this.startTime = value;
      this.handleDateTimeChange();
    },

    handleTooltipShow() {
      this.v2Errors.clear();
      // BTooltip is not propagating the `errors` object up to this component
      this.$refs.tooltip.v2Errors.clear();
      this.setInitialInputValues();
    },

    isTimeValid(timeString) {
      let timeParts;
      if (timeString.includes(':')) {
        timeParts = timeString.split(':');
        if (timeParts.length !== 2) return false;
      } else {
        // Support entering a number (e.g. 615 or 2200)
        if (timeString.length < 3 || timeString.length > 4) return false;
        timeParts = [
          timeString.slice(0, timeString.length - 2),
          timeString.slice(timeString.length - 2),
        ];
      }

      const [hourString, minuteString] = timeParts;
      if (hourString.length < 1 || hourString.length > 2) return false;
      const hourNum = parseInt(hourString, 10);
      if (Number.isNaN(hourNum)) return false;
      if (hourNum < 0 || hourNum > 23) return false;

      if (minuteString.length !== 2) return false;
      const minuteNum = parseInt(minuteString, 10);
      if (Number.isNaN(minuteNum)) return false;
      if (minuteNum < 0 || minuteNum > 59) return false;

      return true;
    },

    openEndDatePicker() {
      this.$refs.endDatePicker.showCalendar();
      this.$refs.endDatePicker.$el.querySelector('input').focus();
    },

    openStartDatePicker() {
      this.$refs.startDatePicker.showCalendar();
      this.$refs.startDatePicker.$el.querySelector('input').focus();
    },

    setInitialInputValues() {
      // Set initial values for date and time
      const endDateTime = this.endDateTime || moment().toDate();
      this.endDate = moment(endDateTime).format(dateFormat);
      this.endTime = moment(endDateTime).format(timeFormat);
      this.startDate = moment(this.startDateTime).format(dateFormat);
      this.startTime = moment(this.startDateTime).format(timeFormat);

      // @todo: Set the input value directly (to fix a <datepicker> bug)
      this.$nextTick(() => {
        if (this.$refs.startDatePicker && this.$refs.endDatePicker) {
          this.$refs.startDatePicker.$el.querySelector('input').value = this.startDate;
          this.$refs.endDatePicker.$el.querySelector('input').value = this.endDate;
        }
      });
    },
  },
};
</script>

<style module>
@import '../../components/FormValidation/css/FormValidationFields.css';

@value (jcGrey700, jcGrey100, jcGrey300) from '~css/brand-colors.css';
@value (lightShadow3) from '~css/shadows.css';

.inputContainer {
  margin-top: 0;
  width: 330px;
}

.applyButton {
  bottom: 0;
  margin: 20px 0 !important;
  position: absolute;
  width: 100%;
}

.datePickerInput {
  margin: 0 !important;
  z-index: 2;
}

.datePickerInput input {
  background-color: transparent !important;
}

/* This element is used since the input value cannot be easily formatted. */
.inputText {
  background-color: white;
  bottom: 1px;
  color: jcGrey700;
  left: 1px;
  overflow: hidden;
  padding: 5px 10px;
  position: absolute;
  right: 1px;
  top: 23px;
  white-space: nowrap;
  z-index: 1;
}

.textSeparator {
  font-weight: bold;
  margin: 0 4px;
}

.datePickerContainer {
  background-color: white;
  border: 1px solid jcGrey300;
  box-shadow: lightShadow3;
  display: flex;
  min-width: 230px;
  padding: 0 20px;
}

.hidden { display: none; }

.inputGroup {
  margin-bottom: 10px;
  position: relative;
}

.dateInput input {
  background: var(--jcWhite);
  border: 2px solid var(--jcGrey200);
  border-radius: 2px;
  line-height: 1.5;
  outline: 0;
  padding: 6px 9px 4px;
  transition: border-color var(--jc-transition-duration) ease-in-out;
  width: 130px;
}

.inputGroup label { font-weight: 400 !important; }
.inputGroup label > input { margin-right: 4px !important; }

.timeInput input[type="text"] {
  display: block;
  width: 64px;
}

.inputLabel {
  composes: label from "~components/FormValidation/css/formFields.css";
  composes: label-color from "~components/FormValidation/css/formFields.css";
  composes: label-font from "~components/FormValidation/css/formFields.css";
  composes: label-position from "~components/FormValidation/css/formFields.css";
  composes: label-typography from "~components/FormValidation/css/formFields.css";
}

.dateInputIcon {
  cursor: pointer;
  height: 12px;
  left: 105px;
  opacity: 0.5;
  position: absolute;
  top: 38px;
  width: 12px;
}

.dateInputIcon:focus { outline: none; }
.dateInputIcon:hover { opacity: 0.75; }

.timePeriodInput {
  left: 70px;
  position: absolute;
  top: 17px;
  width: 68px;
}

.timePeriodInput ul[role="menu"] {
  min-width: auto;
  width: 66px;
}

.timePeriodInput button {
  height: 29px;
}

.calendar {
  left: auto;
  position: relative;
  right: 0;
}

.error {
  max-height: 20px;
  min-height: auto;
}

.quickPickLink {
  display: block;
  font-size: 13px;
  margin-bottom: 20px;
}

.quickPickLink:hover {
  cursor: pointer;
  text-decoration: none !important;
}

.quickPicksContainer {
  border-right: 1px solid jcGrey100;
  width: 125px;
}

.quickPicksHeader {
  margin-bottom: 25px;
}

.sectionHeader {
  color: jcGrey700 !important;
  font-size: 14px !important;
  font-weight: bold !important;
  margin-bottom: 15px !important;
  margin-top: 15px !important;
}

.startEndContainer {
  margin-left: 20px;
  position: relative;
}

.slideTransition,
.slideTransition-enter-active,
.slideTransition-leave-active {
  transition: max-height 0.2s ease-in-out;
}

.slideTransition-enter,
.slideTransition-leave-to {
  max-height: 0;
  overflow: hidden;
}
</style>
